/**
 * Wallet domain — TanStack Query keys, fetchers, and type adapters.
 *
 * Money serialization note: the backend `/wallets/me` returns balances as
 * JavaScript `number` (Drizzle `bigint mode:'number'` — see packages/db/src/columns.ts).
 *
 * BACKEND FOLLOW-UP NEEDED: packages/db/src/columns.ts `money` helper uses
 * `mode: 'number'` — should be `mode: 'bigint'` per CLAUDE.md §2.1. Until that
 * migration lands, all balance/amount fields from svc-wallet arrive as plain
 * `number` in HTTP responses. Tracked as a backend gap, not a frontend bug.
 */
import { apiFetch } from '../../lib/api/client.js';
import { useAuthStore } from '../../stores/auth.js';

export const WALLET_QUERY_KEYS = {
  balances: ['wallet', 'balances'] as const,
  // The date range is part of the key: it is a SERVER filter, so two ranges are
  // two different result sets, not two views of one cache entry.
  transactions: (filter: string, range?: { from?: string; to?: string }) =>
    ['wallet', 'transactions', filter, range?.from ?? null, range?.to ?? null] as const,
} as const;

export interface WalletBalances {
  memberId: string;
  creditCash: number;
  creditPromotion: number;
  commissionWallet: number;
  cashbackWallet: number;
  balance: number;
  withdrawable: number;
  updatedAt: string;
  /**
   * Required. `GET /wallets/me` omits it no longer — while it did, every screen
   * fell through to a literal 'THB' and a JPY member's balance rendered 100×
   * too small (WEB-01). Keeping it optional is what let TypeScript stay quiet
   * about a field that was undefined on every single load.
   */
  currency: string;
}

/**
 * What svc-wallet sends a PLAYER token — deliberately narrower than the ledger row.
 *
 * `description` and `metadata` are absent and re-declaring them will not bring them
 * back: both carry the operator's raw free-text reason for a manual adjustment or a
 * reversal, so the route stops sending them to a player (GitLab issue #146). The
 * backoffice, on an admin token, still receives them. Nothing here rendered either
 * field — `presentTransaction` derives its i18n label key from `type`.
 */
export interface WalletTransaction {
  id: string;
  memberId: string;
  type: string;
  pool: string;
  amount: number;
  balanceAfter: number;
  currency: string;
  refKind: string | null;
  refId: string | null;
  createdAt: string;
}

export interface PaginatedTransactions {
  items: WalletTransaction[];
  total: number;
  page: number;
  pageSize: number;
}

export function fetchWalletBalances(): Promise<WalletBalances> {
  return apiFetch<WalletBalances>('/wallets/me');
}

export function fetchWalletTransactions(params: {
  page?: number;
  pageSize?: number;
  type?: string;
  category?: string;
  /** ISO instants. `GET /wallets/:id/transactions` has accepted both all along. */
  from?: string;
  to?: string;
}): Promise<PaginatedTransactions> {
  const qs = new URLSearchParams();
  if (params.page) qs.set('page', String(params.page));
  if (params.pageSize) qs.set('pageSize', String(params.pageSize));
  if (params.type) qs.set('type', params.type);
  if (params.category) qs.set('category', params.category);
  if (params.from) qs.set('from', params.from);
  if (params.to) qs.set('to', params.to);

  const memberId = useAuthStore.getState().subject?.id ?? 'me';
  return apiFetch<PaginatedTransactions>(`/wallets/${memberId}/transactions?${qs.toString()}`);
}
